import { type MutableRefObject, useCallback } from 'react';

import type { WinchMqttUser } from '../winchMqttApi';
import {
    buildGotoPositionPayload,
    buildSetPositionCommand,
    buildStopPayload,
    buildVerticalScanDisablePayload,
    buildVerticalScanEnableCommand,
    buildVerticalScanSetLimitsCommand,
    winchMqttTopics,
} from '../winchMqttApi';

import type { PendingMeasurementSave, WinchMqttPublish } from './handleWinchMqttMessage';

export type WinchMqttCommands = {
    saveMeasurementPosition: (coords: { distance: number; depth: number }) => void;
    moveToMeasurement: () => void;
    moveToService: () => void;
    stop: () => void;
    disableVerticalScan: () => boolean;
    setVerticalScanLimits: (input: { x: number; yMin: number; yMax: number }) => boolean;
    enableVerticalScan: () => boolean;
};

type UseWinchMqttCommandsInput = {
    canPublish: boolean;
    winchId: number | null;
    publish: WinchMqttPublish;
    userRef: MutableRefObject<WinchMqttUser>;
    pendingMeasurementRef: MutableRefObject<PendingMeasurementSave | null>;
    beginSetPositionPending: () => void;
    beginSetLimitsPending: (requestId: string) => void;
    failSetLimits: () => void;
    onScanEnablePublished: (requestId: string) => void;
};

export function useWinchMqttCommands(input: UseWinchMqttCommandsInput): WinchMqttCommands {
    const {
        canPublish,
        winchId,
        publish,
        userRef,
        pendingMeasurementRef,
        beginSetPositionPending,
        beginSetLimitsPending,
        failSetLimits,
        onScanEnablePublished,
    } = input;

    const saveMeasurementPosition = useCallback(
        (coords: { distance: number; depth: number }) => {
            if (!canPublish || winchId == null) {
                return;
            }
            const command = buildSetPositionCommand({
                user: userRef.current,
                measurement: coords,
            });
            pendingMeasurementRef.current = { ...coords, uuid: command.uuid };
            beginSetPositionPending();
            publish(winchMqttTopics(winchId).publish.setPosition, command.payload, { qos: 1 });
        },
        [beginSetPositionPending, canPublish, pendingMeasurementRef, publish, userRef, winchId]
    );

    const moveToMeasurement = useCallback(() => {
        if (!canPublish || winchId == null) {
            return;
        }
        publish(
            winchMqttTopics(winchId).publish.goto,
            buildGotoPositionPayload({ user: userRef.current, position: 'measurement' }),
            { qos: 1 }
        );
    }, [canPublish, publish, userRef, winchId]);

    const moveToService = useCallback(() => {
        if (!canPublish || winchId == null) {
            return;
        }
        publish(
            winchMqttTopics(winchId).publish.goto,
            buildGotoPositionPayload({ user: userRef.current, position: 'service' }),
            { qos: 1 }
        );
    }, [canPublish, publish, userRef, winchId]);

    const stop = useCallback(() => {
        if (!canPublish || winchId == null) {
            return;
        }
        publish(winchMqttTopics(winchId).publish.stop, buildStopPayload(userRef.current), {
            qos: 1,
        });
    }, [canPublish, publish, userRef, winchId]);

    const disableVerticalScan = useCallback((): boolean => {
        if (!canPublish || winchId == null) {
            return false;
        }
        publish(
            winchMqttTopics(winchId).publish.verticalScanDisable,
            buildVerticalScanDisablePayload(userRef.current),
            { qos: 1 }
        );
        return true;
    }, [canPublish, publish, userRef, winchId]);

    const setVerticalScanLimits = useCallback(
        (limits: { x: number; yMin: number; yMax: number }): boolean => {
            if (!canPublish || winchId == null) {
                failSetLimits();
                return false;
            }
            const command = buildVerticalScanSetLimitsCommand({
                user: userRef.current,
                ...limits,
            });
            beginSetLimitsPending(command.uuid);
            publish(winchMqttTopics(winchId).publish.verticalScanSetLimits, command.payload, {
                qos: 1,
            });
            return true;
        },
        [beginSetLimitsPending, canPublish, failSetLimits, publish, userRef, winchId]
    );

    const enableVerticalScan = useCallback((): boolean => {
        if (!canPublish || winchId == null) {
            return false;
        }
        const command = buildVerticalScanEnableCommand(userRef.current);
        publish(winchMqttTopics(winchId).publish.verticalScanEnable, command.payload, {
            qos: 1,
        });
        onScanEnablePublished(command.uuid);
        return true;
    }, [canPublish, onScanEnablePublished, publish, userRef, winchId]);

    return {
        saveMeasurementPosition,
        moveToMeasurement,
        moveToService,
        stop,
        disableVerticalScan,
        setVerticalScanLimits,
        enableVerticalScan,
    };
}
